import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  setDoc,
  writeBatch
} from 'firebase/firestore';
import { competitionEndInstant, isLogInCompetitionWindow, type CompetitionWindow } from '@/lib/competitions/competitionDates';
import { beneficiaryCompetitions, habitTallies, type BeneficiaryCompetition } from '@/lib/competitions/beneficiaryCompetitions';
import { db } from '@/lib/firebase/config';
import type { Competition, CompetitionLog, CompetitionStats } from '@/types/competitions';
import { format } from 'date-fns';

// Check if organization has any overlapping competitions
export async function hasOverlappingCompetition(
  organizationId: string,
  startDate: Date,
  endDate: Date,
  excludeId?: string
): Promise<boolean> {
  try {
    // Get all competitions for this organization
    const q = query(
      collection(db, 'competitions'),
      where('organizationId', '==', organizationId)
    );

    const querySnapshot = await getDocs(q);

    // Check each competition for time overlap
    for (const doc of querySnapshot.docs) {
      if (excludeId && doc.id === excludeId) {
        continue;
      }

      const competition = doc.data();
      const competitionStart = competition.startDate.toDate();
      const competitionEnd = competition.endDate.toDate();
      
      // Check if there's any overlap between the date ranges
      // Overlap occurs if: startDate < competitionEnd AND endDate > competitionStart
      if (startDate < competitionEnd && endDate > competitionStart) {
        return true; // Found an overlapping competition
      }
    }
    
    return false; // No overlapping competitions found
  } catch (error) {
    console.error('Error checking for overlapping competitions:', error);
    throw error;
  }
}

// Check if organization has an active competition (kept for backward compatibility)
export async function hasActiveCompetition(organizationId: string): Promise<boolean> {
  try {
    const now = new Date();
    return await hasOverlappingCompetition(organizationId, now, now);
  } catch (error) {
    console.error('Error checking for active competitions:', error);
    throw error;
  }
}

// Competition Management
export async function createCompetition(input: Omit<Competition, 'id' | 'createdAt' | 'updatedAt' | 'status'>): Promise<string> {
  try {
    const data = { ...input, endDate: competitionEndInstant(input.endDate) };
    // Check if organization has any overlapping competitions
    const hasOverlapping = await hasOverlappingCompetition(data.organizationId, data.startDate, data.endDate);
    if (hasOverlapping) {
      throw new Error('Organization already has a competition that overlaps with these dates. Competitions cannot have overlapping time periods.');
    }

    const now = new Date();
    const competitionData = {
      ...data,
      // Default to published so existing behavior (competition immediately
      // visible to beneficiaries) is preserved unless the manager opts out.
      isPublished: data.isPublished ?? true,
      startDate: Timestamp.fromDate(data.startDate),
      endDate: Timestamp.fromDate(data.endDate),
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
      status: determineCompetitionStatus(data.startDate, data.endDate)
    };

    const docRef = await addDoc(collection(db, 'competitions'), competitionData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating competition:', error);
    throw error;
  }
}

export async function updateCompetition(
  competitionId: string,
  input: {
    name: string;
    description?: string;
    startDate: Date;
    endDate: Date;
    habits: Competition['habits'];
    // Optional: when omitted, the existing isPublished value is left untouched.
    isPublished?: boolean;
  }
): Promise<void> {
  try {
    const data = { ...input, endDate: competitionEndInstant(input.endDate) };
    const existing = await getCompetition(competitionId);
    if (!existing) {
      throw new Error('Competition not found');
    }

    // Check if organization has any overlapping competitions, excluding this one
    const hasOverlapping = await hasOverlappingCompetition(
      existing.organizationId,
      data.startDate,
      data.endDate,
      competitionId
    );
    if (hasOverlapping) {
      throw new Error('Organization already has a competition that overlaps with these dates. Competitions cannot have overlapping time periods.');
    }

    const competitionRef = doc(db, 'competitions', competitionId);
    const updatePayload: Record<string, unknown> = {
      name: data.name,
      description: data.description,
      startDate: Timestamp.fromDate(data.startDate),
      endDate: Timestamp.fromDate(data.endDate),
      habits: data.habits,
      updatedAt: Timestamp.fromDate(new Date()),
      status: determineCompetitionStatus(data.startDate, data.endDate)
    };

    // Only touch isPublished when the caller explicitly supplied it, so
    // callers that don't care about publish state (e.g. status refresh
    // jobs) can't accidentally flip it.
    if (data.isPublished !== undefined) {
      updatePayload.isPublished = data.isPublished;
    }

    await updateDoc(competitionRef, updatePayload);
  } catch (error) {
    console.error('Error updating competition:', error);
    throw error;
  }
}

// Delete a competition and cascade-delete every record that references it.
// Firestore batched writes are capped at 500 operations; we chunk at 450 to
// stay safely under that limit, looping until every matching log is gone.
/**
 * Delete every competitionsLogs doc belonging to one competition and return how
 * many were removed. Looping (rather than one giant batch) keeps each batch
 * under Firestore's 500-op cap regardless of how many logs exist.
 *
 * The query filters on competitionId ONLY — never on a shared field such as
 * organizationId — so this can never reach another competition's logs. The
 * competition document itself is untouched.
 */
export async function resetCompetitionLogs(competitionId: string): Promise<number> {
  const BATCH_LIMIT = 450;
  let deleted = 0;

  for (;;) {
    const logsQuery = query(
      collection(db, 'competitionsLogs'),
      where('competitionId', '==', competitionId),
      limit(BATCH_LIMIT)
    );
    const logsSnapshot = await getDocs(logsQuery);

    if (logsSnapshot.empty) {
      break;
    }

    const batch = writeBatch(db);
    logsSnapshot.docs.forEach((logDoc) => {
      batch.delete(logDoc.ref);
    });
    await batch.commit();
    deleted += logsSnapshot.size;

    if (logsSnapshot.size < BATCH_LIMIT) {
      break;
    }
  }

  return deleted;
}

export async function deleteCompetition(competitionId: string): Promise<void> {
  try {
    // Cascade delete: clear the logs first (the same loop a reset uses), then
    // remove the competition document itself.
    await resetCompetitionLogs(competitionId);

    const competitionRef = doc(db, 'competitions', competitionId);
    await deleteDoc(competitionRef);
  } catch (error) {
    console.error('Error deleting competition:', error);
    throw error;
  }
}

export async function getCompetition(competitionId: string): Promise<Competition | null> {
  try {
    const docRef = doc(db, 'competitions', competitionId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      startDate: data.startDate?.toDate() || new Date(),
      endDate: data.endDate?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as Competition;
  } catch (error) {
    console.error('Error getting competition:', error);
    throw error;
  }
}

export async function getOrganizationCompetitions(organizationId: string): Promise<Competition[]> {
  try {
    const q = query(
      collection(db, 'competitions'),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        startDate: data.startDate?.toDate() || new Date(),
        endDate: data.endDate?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as Competition;
    });
  } catch (error) {
    console.error('Error getting organization competitions:', error);
    throw error;
  }
}

export async function getActiveCompetitionsForUser(userId: string, organizationId: string): Promise<Competition[]> {
  try {
    const now = new Date();
    const q = query(
      collection(db, 'competitions'),
      where('organizationId', '==', organizationId),
      where('startDate', '<=', Timestamp.fromDate(now)),
      where('endDate', '>=', Timestamp.fromDate(now))
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as Competition;
      })
      // Beneficiary-facing read: exclude unpublished competitions. A missing
      // isPublished field means the competition predates this flag and must
      // still be treated as published (backward compatibility).
      .filter(competition => competition.isPublished !== false);
  } catch (error) {
    console.error('Error fetching active competitions for user:', error);
    throw error;
  }
}

/**
 * Beneficiary view: running competitions plus those that ended within
 * ENDED_VISIBLE_DAYS, each flagged `hasEnded` (namat#33). Organization-only
 * query, so no composite index; the date rule lives in beneficiaryCompetitions.
 */
export async function getBeneficiaryCompetitions(organizationId: string): Promise<BeneficiaryCompetition<Competition>[]> {
  const snapshot = await getDocs(query(collection(db, 'competitions'), where('organizationId', '==', organizationId)));
  const list = snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      startDate: data.startDate?.toDate() || new Date(),
      endDate: data.endDate?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as Competition;
  });
  return beneficiaryCompetitions(list, new Date());
}

/** How many days this user completed each habit within the competition's current dates (namat#34). */
export async function getUserCompetitionHabitTallies(
  competitionId: string,
  userId: string,
  window: CompetitionWindow
): Promise<Record<string, number>> {
  const snapshot = await getDocs(query(
    collection(db, 'competitionsLogs'),
    where('competitionId', '==', competitionId),
    where('userId', '==', userId)
  ));
  return habitTallies(
    snapshot.docs
      .map(d => d.data() as { habitId: string; completed?: boolean; date?: string })
      .filter(log => isLogInCompetitionWindow(log.date, window))
  );
}

/**
 * Which of these habits already have at least one competitionsLogs doc
 * (namat#25): their category is locked in the editor. One `limit(1)` read
 * per habit, so the cost does not grow with the number of logs.
 */
export async function getHabitIdsWithLogs(competitionId: string, habitIds: string[]): Promise<Set<string>> {
  const found = await Promise.all(
    habitIds.map(async (habitId) => {
      const snap = await getDocs(query(
        collection(db, 'competitionsLogs'),
        where('competitionId', '==', competitionId),
        where('habitId', '==', habitId),
        limit(1)
      ));
      return snap.empty ? null : habitId;
    })
  );
  return new Set(found.filter((id): id is string => id !== null));
}

// Competition Logging
export async function logCompetitionHabit(
  competitionId: string,
  habitId: string,
  userId: string,
  organizationId: string,
  habitName: string,
  habitCategory: string,
  source: 'manual' | 'ai_followup' = 'manual'
): Promise<void> {
  try {
    const today = format(new Date(), 'yyyy-MM-dd');
    const logId = `${competitionId}_${habitId}_${userId}_${today}`;
    
    // Check if already logged today
    const existingLogRef = doc(db, 'competitionsLogs', logId);
    const existingLog = await getDoc(existingLogRef);
    
    if (existingLog.exists() && existingLog.data().completed) {
      throw new Error('Competition habit already logged today');
    }

    const logData: Omit<CompetitionLog, 'id'> = {
      competitionId,
      habitId,
      userId,
      date: today,
      completed: true,
      timestamp: new Date(),
      habitName,
      habitCategory,
      organizationId,
      source,
      metadata: {
        loggedFromApp: source === 'manual',
        loggedFromAI: source === 'ai_followup'
      }
    };

    await setDoc(existingLogRef, logData);
  } catch (error) {
    console.error('Error logging competition habit:', error);
    throw error;
  }
}

export async function unlogCompetitionHabit(
  competitionId: string,
  habitId: string,
  userId: string
): Promise<void> {
  try {
    const today = format(new Date(), 'yyyy-MM-dd');
    const logId = `${competitionId}_${habitId}_${userId}_${today}`;
    
    const logRef = doc(db, 'competitionsLogs', logId);
    await deleteDoc(logRef);
  } catch (error) {
    console.error('Error unlogging competition habit:', error);
    throw error;
  }
}

export async function isCompetitionHabitLoggedToday(
  competitionId: string,
  habitId: string,
  userId: string
): Promise<boolean> {
  try {
    const today = format(new Date(), 'yyyy-MM-dd');
    const logId = `${competitionId}_${habitId}_${userId}_${today}`;
    
    const logRef = doc(db, 'competitionsLogs', logId);
    const logSnap = await getDoc(logRef);
    
    return logSnap.exists() && logSnap.data().completed === true;
  } catch (error) {
    console.error('Error checking competition habit log:', error);
    return false;
  }
}

export async function getCompetitionLogs(
  competitionId: string,
  userId?: string,
  habitId?: string,
  days: number = 30
): Promise<CompetitionLog[]> {
  try {
    let q = query(collection(db, 'competitionsLogs'));
    
    // Add filters
    q = query(q, where('competitionId', '==', competitionId));
    
    if (userId) {
      q = query(q, where('userId', '==', userId));
    }
    
    if (habitId) {
      q = query(q, where('habitId', '==', habitId));
    }
    
    // Add date range filter
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    q = query(
      q,
      where('date', '>=', format(startDate, 'yyyy-MM-dd')),
      where('date', '<=', format(endDate, 'yyyy-MM-dd')),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate() || new Date()
    } as CompetitionLog));
  } catch (error) {
    console.error('Error getting competition logs:', error);
    return [];
  }
}

export async function getCompetitionStats(competitionId: string, userId: string): Promise<CompetitionStats | null> {
  try {
    const competition = await getCompetition(competitionId);
    if (!competition) return null;

    const logs = await getCompetitionLogs(competitionId, userId);
    const completedLogs = logs.filter(log => log.completed);
    
    const totalHabits = competition.habits.length;
    const completedHabits = completedLogs.length;
    const completionPercentage = totalHabits > 0 ? Math.round((completedHabits / totalHabits) * 100) : 0;
    
    // Calculate current streak
    let currentStreak = 0;
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() - i);
      const dateStr = format(checkDate, 'yyyy-MM-dd');
      
      const logForDate = logs.find(log => log.date === dateStr && log.completed);
      if (logForDate) {
        currentStreak++;
      } else {
        break;
      }
    }
    
    // Calculate longest streak
    let longestStreak = 0;
    let tempStreak = 0;
    const sortedLogs = logs.sort((a, b) => a.date.localeCompare(b.date));
    
    for (const log of sortedLogs) {
      if (log.completed) {
        tempStreak++;
        longestStreak = Math.max(longestStreak, tempStreak);
      } else {
        tempStreak = 0;
      }
    }
    
    const lastLoggedAt = completedLogs.length > 0 
      ? new Date(Math.max(...completedLogs.map(log => log.timestamp.getTime())))
      : undefined;

    return {
      competitionId,
      userId,
      totalHabits,
      completedHabits,
      completionPercentage,
      currentStreak,
      longestStreak,
      lastLoggedAt
    };
  } catch (error) {
    console.error('Error getting competition stats:', error);
    return null;
  }
}

// Helper function to determine competition status
function determineCompetitionStatus(startDate: Date, endDate: Date): 'upcoming' | 'active' | 'completed' {
  const now = new Date();
  
  if (now < startDate) {
    return 'upcoming';
  } else if (now >= startDate && now <= endDate) {
    return 'active';
  } else {
    return 'completed';
  }
}

// Update competition status (should be called periodically)
export async function updateCompetitionStatus(competitionId: string): Promise<void> {
  try {
    const competition = await getCompetition(competitionId);
    if (!competition) return;

    const newStatus = determineCompetitionStatus(competition.startDate, competition.endDate);
    
    if (competition.status !== newStatus) {
      const competitionRef = doc(db, 'competitions', competitionId);
      await updateDoc(competitionRef, {
          status: newStatus,
        updatedAt: Timestamp.fromDate(new Date())
        });
      }
  } catch (error) {
    console.error('Error updating competition status:', error);
    throw error;
  }
}
